import { getLocalTimeZone, now } from "@internationalized/date"
import { captureException } from "@sentry/react"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useRef, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import ConfirmPlateModal from "@/globalComponents/molecules/ConfirmPlateModal"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import VehicleSelectorField from "@/globalComponents/molecules/VehicleSelectorField"
import {
  ExpectedError,
  ParkPassRedeemError,
  ParkPlateError,
} from "@/utilities/expectedErrors"
import { licensePlateValidationRule, requiredRule } from "@/utilities/form"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import { log } from "@/utilities/logging"
import { normalizeLicensePlate } from "@/utilities/normalizers"

import { LotMismatchWarningModal } from "../-components/LotMismatchWarningModal"
import ActiveSessionModal from "../../park/$epoch/-components/ActiveSessionModal"

// TODO fix me, we should just have the passID baked into the url
export const Route = createFileRoute(
  "/qr/$qrLookupID/passes/$epoch/redeem/$passID/"
)({
  staticData: {
    pageName: "Redeem Pass",
  },
  beforeLoad: ({ context, params, location }) => {
    /*
      TODO fix me, we shouldn't pass manually created links!
      Use the tools given by the router to make it typesafe!!!
    */
    if (!context.isAuthenticated) {
      throw redirect({
        to: "/auth/sign-in",
        search: {
          qrLookupID: params.qrLookupID,
          redirect: location.href,
          plate: undefined,
          phone: undefined,
          conversionFlow: undefined,
          email: undefined,
        },
      })
    }
  },
  component: RouteComponent,
})

interface FlexRedeemFormSchema {
  licensePlate: string
  selectedSavedPlate: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID, passID } = Route.useParams()
  const { t } = useTranslation(["passes", "park", "errors", "common"])
  const [showConfirmModal, toggleConfirmModal] = useToggle(false)
  const [showActiveSessionModal, toggleActiveSessionModal] = useToggle(false)
  const [showLotMismatchModal, toggleLotMismatchModal] = useToggle(false)
  const [canExtendSession, setCanExtendSession] = useState(false)
  const [lotMismatchData, setLotMismatchData] = useState<{
    qrLotName: string
    cameraLotName: string
  } | null>(null)
  const [pendingFormData, setPendingFormData] =
    useState<FlexRedeemFormSchema | null>(null)
  const licensePlateRef = useRef<HTMLInputElement>(null)

  // Get user data
  const { data: me } = useSuspenseQuery(meQueryOptions())

  const savedPlates = me?.inflowUser.savedPlates || []

  const form = useForm<FlexRedeemFormSchema>({
    defaultValues: {
      licensePlate: "",
      selectedSavedPlate: savedPlates.length ? savedPlates[0].plate : "",
    },
    mode: "onBlur",
  })

  const { mutate: redeemPass, isPending } = useMutation({
    mutationFn: async ({
      data,
      skipPlateCall = false,
    }: {
      data: FlexRedeemFormSchema
      skipPlateCall?: boolean
    }) => {
      // Get Cognito ID token
      const { cognitoIDToken } = await api.auth.fetchAuthSession()
      if (!cognitoIDToken) {
        throw new Error("Failed to get authentication token")
      }

      // Skip plate call if user already acknowledged lot mismatch warning
      if (!skipPlateCall) {
        // First, register the plate with the session using /plate endpoint
        const plateResponse = await api.park.plate.POST({
          plate: data.licensePlate,
        })

        if ("failReason" in plateResponse) {
          throw new ParkPlateError(plateResponse.failReason)
        }

        // Check for active session (already paid)
        if (plateResponse.alreadyPaid) {
          return { plateResponse, redeemResponse: null, showLotMismatch: false }
        }

        // Check for lot mismatch warning (unless user already acknowledged it)
        if (plateResponse.warning && plateResponse.warningData) {
          return {
            plateResponse,
            redeemResponse: null,
            showLotMismatch: true,
            lotMismatchData: {
              qrLotName: plateResponse.warningData.qrLotName,
              cameraLotName: plateResponse.warningData.cameraLotName,
            },
          }
        }
      }

      // Phone is required for account, but fallback to email just in case
      const phone = me?.cognitoUser?.phone_number || ""
      const email = me?.cognitoUser?.email || ""
      const contact = phone || email

      // Call pass-redeem API
      const redeemResponse = await api.park.passRedeem.POST({
        cognitoIDToken,
        passID: passID || "",
        termsAndConditionsOKTimestamp:
          now(getLocalTimeZone()).toAbsoluteString(),
        phoneNumber: phone,
        email: phone ? "" : email,
      })

      if ("failReason" in redeemResponse) {
        throw new ParkPassRedeemError(redeemResponse.failReason)
      }

      return {
        plateResponse: null,
        redeemResponse,
        showLotMismatch: false,
        contact,
      }
    },
    onSuccess: async (result, variables) => {
      const { plateResponse, redeemResponse, showLotMismatch } = result

      // Check for active session (already paid)
      if (plateResponse?.alreadyPaid) {
        setPendingFormData(variables.data)
        setCanExtendSession(!!plateResponse.alreadyPaidData?.canExtendSession)
        toggleActiveSessionModal()
        return
      }

      // Check for lot mismatch warning
      if (
        showLotMismatch &&
        "lotMismatchData" in result &&
        result.lotMismatchData
      ) {
        setPendingFormData(variables.data)
        setLotMismatchData(result.lotMismatchData)
        toggleLotMismatchModal()
        return
      }

      // Success - redeem complete
      if (!redeemResponse) return

      navigate({
        to: "/qr/$qrLookupID/passes/$epoch/redeem/$passID/confirmation",
        search: {
          contact: result.contact,
        },
      })
    },
    onError: (error, variables) => {
      log({
        level: "error",
        flow: "passes:redeem",
        scope: "validation",
        message: "Pass redemption failed",
        meta: {
          passID,
          licensePlate: variables.data.licensePlate,
          qrLookupID,
          error: error.message,
        },
      })
      captureException(error, {
        extra: {
          passID,
          licensePlate: variables.data.licensePlate,
          qrLookupID,
        },
        tags: {
          section: "passes-redeem",
          flow: "pass-redemption",
        },
      })

      if (error instanceof ParkPlateError) {
        form.setError("licensePlate", {
          type: "server",
          message: t(error.translationKey, { ns: "errors" }),
        })
      } else if (error instanceof ExpectedError) {
        form.setError("root", {
          type: "server",
          message: t(error.translationKey, { ns: "errors" }),
        })
      } else {
        form.setError("root", {
          type: "server",
          message: t("common.somethingWentWrong", { ns: "errors" }),
        })
      }
    },
  })

  return (
    <>
      <Heading level={2} as={1} className="center">
        {t("redeem.header")}
      </Heading>

      <Card>
        <Heading level={4} className="center">
          {t("redeem.description")}
        </Heading>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={isPending}>
              {/* Vehicle Selection */}
              {savedPlates.length > 0 ? (
                <Controller
                  name="selectedSavedPlate"
                  control={form.control}
                  render={({ field }) => (
                    <VehicleSelectorField {...field}>
                      <Label>{t("register.savedPlates", { ns: "park" })}</Label>
                      {savedPlates.map((vehicle) => (
                        <VehicleSelectorField.Radio
                          vehicle={vehicle}
                          key={`${vehicle.lastUsed}-${vehicle.nickname}-${vehicle.plate}`}
                        />
                      ))}
                      <VehicleSelectorField.Radio vehicle={undefined} />
                    </VehicleSelectorField>
                  )}
                />
              ) : (
                <input type="hidden" {...form.register("selectedSavedPlate")} />
              )}

              {/* License Plate Input (when entering new plate) */}
              {form.watch("selectedSavedPlate") === "" && (
                <Controller
                  name="licensePlate"
                  control={form.control}
                  rules={{
                    required: requiredRule(
                      t("common.required", { ns: "errors" })
                    ),
                    validate: (value) => {
                      const normalized = normalizeLicensePlate(value)
                      return licensePlateValidationRule(t)(normalized)
                    },
                  }}
                  render={({
                    field: { ref, value, onChange, ...rest },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      {...rest}
                      isInvalid={invalid}
                      onChange={(v) => onChange(normalizeLicensePlate(v))}
                      value={typeof value === "string" ? value : ""}
                      onFocus={() => form.clearErrors("root")}
                      aria-label={t("redeem.licensePlate")}
                    >
                      <Label>{t("redeem.licensePlate")}</Label>
                      <TextField.Input
                        innerRef={(el) => {
                          ref(el)
                          licensePlateRef.current = el
                        }}
                        placeholder="ABC123"
                        maxLength={12}
                        style={{ textTransform: "uppercase" }}
                      />
                      <FieldError>{error?.message}</FieldError>
                    </TextField>
                  )}
                />
              )}

              {/* Root Error */}
              {form.formState.errors.root && (
                <FieldError className="center">
                  {form.formState.errors.root.message}
                </FieldError>
              )}

              {/* Terms and Conditions Text */}
              <Text
                level={2}
                className="center"
                style={{ marginTop: "var(--space-4)" }}
              >
                <TermsAndConditions qrLookupID={qrLookupID} />
              </Text>

              {/* Submit Button */}
              <Button type="submit" buttonType="primary" isPending={isPending}>
                {t("redeem.agreeAndContinue")}
              </Button>
            </fieldset>
          </form>
        </FormProvider>
      </Card>

      {/* Plate Confirmation Modal */}
      <ConfirmPlateModal
        isOpen={showConfirmModal}
        onOpenChange={toggleConfirmModal}
        plate={pendingFormData?.licensePlate || ""}
        title={t("register.confirmPlate", { ns: "park" })}
        description={t("register.confirmPlateDescription", { ns: "park" })}
        editButtonText={t("register.editPlate", { ns: "park" })}
        confirmButtonText={t("register.continue", { ns: "park" })}
        onConfirm={handleConfirmPlate}
        onEdit={handleEditPlate}
      />

      {/* Active Session Modal */}
      <ActiveSessionModal
        isOpen={showActiveSessionModal}
        onOpenChange={toggleActiveSessionModal}
        canExtend={canExtendSession}
        onExtend={undefined}
        qrLookupID={qrLookupID}
        isFlexParkFlow={true}
      />

      {/* Lot Mismatch Warning Modal */}
      {lotMismatchData && (
        <LotMismatchWarningModal
          isOpen={showLotMismatchModal}
          onOpenChange={(isOpen) => {
            toggleLotMismatchModal()
            if (!isOpen) setLotMismatchData(null)
          }}
          qrLotName={lotMismatchData.qrLotName}
          cameraLotName={lotMismatchData.cameraLotName}
          onProceed={handleLotMismatchProceed}
        />
      )}
    </>
  )

  function onSubmit(data: FlexRedeemFormSchema) {
    // Get the plate to use (either selected saved plate or new license plate)
    const plateToUse = data.selectedSavedPlate || data.licensePlate

    if (!plateToUse) {
      form.setError("root", {
        type: "required",
        message: t("redeem.selectOrEnterPlate"),
      })
      return
    }

    // Update licensePlate field with the actual plate being used
    const formData: FlexRedeemFormSchema = {
      ...data,
      licensePlate: plateToUse,
    }

    setPendingFormData(formData)
    if (!showConfirmModal) toggleConfirmModal()
  }

  function handleConfirmPlate() {
    if (pendingFormData) {
      if (showConfirmModal) toggleConfirmModal()
      redeemPass({ data: pendingFormData })
    }
  }

  function handleEditPlate() {
    if (showConfirmModal) toggleConfirmModal()
  }

  function handleLotMismatchProceed() {
    if (showLotMismatchModal) toggleLotMismatchModal()
    if (pendingFormData) {
      // Skip the /plate call since we already validated the plate and user acknowledged the warning
      redeemPass({ data: pendingFormData, skipPlateCall: true })
    }
  }
}
